import { ChangeEvent, FC } from "react"
import classNames from "classnames"
import { useSelector } from "react-redux"
import Actions from "./Actions"
import { AddressInfo } from "../AddressInfo"
import Cell from "../Cell"
import TextField from "../TextField"
import Spinner, { SpinnerSize } from "@/components/shared/Spinner"
import useFormStateHandler from "@/hooks/useFormStateHandler"
import { fieldConfig } from "@/pages/ControlPanel/config"
import { RootState } from "@/store"
import { FacilityAddress, FacilityForm } from "@/types/facility"
import { getErrorMessage } from "@/utils/index"

interface NewFacilityProps {
   onSave: (facilityForm: FacilityForm) => void
   cancel: () => void
}

export const initialFormData: FacilityForm = {
   name: "",
   address: {
      country: "",
      state: "",
      city: "",
      zipCode: "",
      street: "",
      timezone: null,
   },
   entraTid: "",
}

const NewFacility: FC<NewFacilityProps> = ({ onSave, cancel }) => {
   const { loadingFacility, errorFacility } = useSelector(
      ({ facilitiesReducer }: RootState) => facilitiesReducer
   )
   const [facilityFormState, setState] =
      useFormStateHandler<FacilityForm>(initialFormData)

   const isFacilityNameValid = fieldConfig.name?.validate(
      facilityFormState.name
   )

   const isCountryValid = fieldConfig.country?.validate(
      facilityFormState.address?.country || "USA"
   )

   const isEntraTidValid = fieldConfig.entraTid?.validate(
      facilityFormState.entraTid || ""
   )

   const handleFacilityFieldChange =
      (field: keyof FacilityForm) => (e: ChangeEvent<HTMLInputElement>) => {
         setState({
            ...facilityFormState,
            [field]: e.target.value,
         })
      }

   const handleFacilityNameChange = handleFacilityFieldChange("name")
   const handleEntraTidChange = handleFacilityFieldChange("entraTid")

   return (
      <div
         className={classNames("customers-facilities-table__group--facility", {
            "customers-facilities-table__group--loading": loadingFacility,
            "customers-facilities-table__group--error": errorFacility,
         })}
      >
         {loadingFacility ? (
            <Spinner size={SpinnerSize.SMALL} />
         ) : errorFacility ? (
            getErrorMessage(errorFacility)
         ) : (
            <>
               <span className="customers-facilities-table__facility-info">
                  <span className="customers-facilities-table__facility-name">
                     <Cell
                        name="name"
                        value={facilityFormState.name}
                        isEditMode
                        handleCellChange={handleFacilityNameChange}
                        placeholder={fieldConfig["name"]?.placeholder}
                     >
                        <TextField name="name">
                           {facilityFormState.name}
                        </TextField>
                     </Cell>
                  </span>
                  <AddressInfo
                     isEditMode
                     id=""
                     address={
                        facilityFormState.address || ({} as FacilityAddress)
                     }
                     onChange={(e) => {
                        setState({
                           ...facilityFormState,
                           address: {
                              ...facilityFormState.address,
                              [e.target.name]: e.target.value,
                           } as FacilityAddress,
                        })
                     }}
                     onStateChange={(selectedState) => {
                        setState({
                           ...facilityFormState,
                           address: {
                              ...facilityFormState.address,
                              state: selectedState,
                           } as FacilityAddress,
                        })
                     }}
                     onTimezoneChange={(timezone) => {
                        setState({
                           ...facilityFormState,
                           address: {
                              ...facilityFormState.address,
                              timezone,
                           } as FacilityAddress,
                        })
                     }}
                  />
                  <span className="customers-facilities-table__entra-tid">
                     <Cell
                        name="entraTid"
                        value={facilityFormState.entraTid || ""}
                        isEditMode
                        error={!isEntraTidValid}
                        handleCellChange={handleEntraTidChange}
                        placeholder={fieldConfig.entraTid?.placeholder}
                     >
                        <TextField name="entraTid">
                           {facilityFormState.entraTid || "-"}
                        </TextField>
                     </Cell>
                  </span>
               </span>
               <Actions
                  isFormValid={
                     (isFacilityNameValid &&
                        isCountryValid &&
                        isEntraTidValid) ||
                     false
                  }
                  onSave={() => {
                     const formWithUSA = {
                        ...facilityFormState,
                        address: {
                           ...facilityFormState.address,
                           country: "USA",
                        } as FacilityAddress,
                     }
                     onSave(formWithUSA)
                  }}
                  onCancel={cancel}
               />
            </>
         )}
      </div>
   )
}

export default NewFacility
